import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { INK, OLIVE, BODY } from './DagbladFrontPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export function useRewards() {
  const [data, setData] = useState(null);
  const load = useCallback(() => axios.get(`${API}/dagblad/rewards`, cfg).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener('focus-critter:rewards-changed', load);
    return () => window.removeEventListener('focus-critter:rewards-changed', load);
  }, [load]);
  return data;
}

const changed = () => window.dispatchEvent(new Event('focus-critter:rewards-changed'));

export function KidRewards({ kid, rewards }) {
  const [busy, setBusy] = useState(false);
  const bal = rewards?.kids.find((k) => k.id === kid.id);
  if (!rewards || !bal) return null;
  const pending = rewards.requests.filter((r) => r.kid_id === kid.id && r.status === 'pending');
  const ask = async (r) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/dagblad/rewards/${r.id}/request`, { kid_id: kid.id }, cfg);
      toast.success(`${r.emoji} ${kid.name} vill byta till ${r.title}! Väntar på att du godkänner ✋`);
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte byta'); }
    setBusy(false);
  };
  return (
    <div className="mt-3 pt-2 border-t border-dotted" style={{ borderColor: OLIVE }} data-testid={`kidcol-${kid.id}-rewards`}>
      <p className="text-xs font-black" style={{ color: OLIVE }} data-testid={`kidcol-${kid.id}-balance`}>🎁 {bal.balance} att byta{bal.reserved ? ` · ${bal.reserved} väntar` : ''}</p>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {rewards.rewards.map((r) => {
          const left = r.cost - bal.balance;
          return (
            <button key={r.id} onClick={() => ask(r)} disabled={left > 0 || busy}
              className="no-print text-[11px] font-bold px-2 py-1 border-2 transition-transform hover:scale-105 disabled:hover:scale-100"
              style={{ borderColor: INK, background: left > 0 ? 'transparent' : '#FFE8A3', opacity: left > 0 ? 0.55 : 1, fontFamily: BODY }}
              title={left > 0 ? `${left} klistermärken kvar` : 'Byt nu!'} data-testid={`kidcol-${kid.id}-reward-${r.id}`}>
              {r.emoji} {r.title} · {r.cost}{left > 0 ? ` (${left} kvar)` : ' ✨'}
            </button>
          );
        })}
      </div>
      {pending.map((r) => (
        <p key={r.id} className="text-[11px] italic mt-1" style={{ fontFamily: BODY }} data-testid={`kidcol-${kid.id}-pending-${r.id}`}>✋ {r.emoji} {r.title} väntar på godkännande</p>
      ))}
    </div>
  );
}

export function RewardsAdmin({ rewards }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', emoji: '🎁', cost: 10 });
  if (!rewards) return null;
  const pending = rewards.requests.filter((r) => r.status === 'pending');
  const decide = async (r, action) => {
    try {
      await axios.post(`${API}/dagblad/rewards/requests/${r.id}/${action}`, {}, cfg);
      toast.success(action === 'approve' ? `🎉 ${r.kid_name} får ${r.emoji} ${r.title}!` : `${r.kid_name}s byte är nekat, klistermärkena är tillbaka`);
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dagblad/rewards`, { ...form, cost: Number(form.cost) }, cfg);
      setForm({ title: '', emoji: '🎁', cost: 10 });
      changed();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Kunde inte lägga till'); }
  };
  const del = async (r) => { await axios.delete(`${API}/dagblad/rewards/${r.id}`, cfg).catch(() => {}); changed(); };
  return (
    <div className="no-print mt-3 p-3 border-2" style={{ borderColor: INK, background: '#FBF7EE' }} data-testid="rewards-admin">
      {pending.length > 0 && (
        <div className="mb-2" data-testid="rewards-pending">
          <p className="text-[11px] font-black uppercase tracking-widest mb-1" style={{ color: '#8C2F2F' }}>Väntar på dig</p>
          {pending.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm py-1" style={{ fontFamily: BODY }} data-testid={`rewards-request-${r.id}`}>
              <span className="flex-1">{r.kid_name} vill byta {r.cost} ⭐ mot {r.emoji} {r.title}</span>
              <button onClick={() => decide(r, 'approve')} className="px-2 py-1 text-xs font-black text-[#F4EEE1]" style={{ background: OLIVE }} data-testid={`rewards-approve-${r.id}`}>Godkänn</button>
              <button onClick={() => decide(r, 'decline')} className="px-2 py-1 text-xs font-black border-2" style={{ borderColor: INK }} data-testid={`rewards-decline-${r.id}`}>Neka</button>
            </div>
          ))}
        </div>
      )}
      <button onClick={() => setOpen(!open)} className="text-xs font-black underline underline-offset-4" style={{ color: OLIVE }} data-testid="rewards-manage-open">
        🎁 {open ? 'Stäng belöningar' : 'Ändra belöningar och priser'}
      </button>
      {open && (
        <div className="mt-2" data-testid="rewards-manage">
          {rewards.rewards.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm py-0.5" style={{ fontFamily: BODY }} data-testid={`rewards-item-${r.id}`}>
              <span className="flex-1">{r.emoji} {r.title} · {r.cost} klistermärken</span>
              <button onClick={() => del(r)} className="text-xs opacity-50 hover:opacity-100" aria-label="Ta bort" data-testid={`rewards-delete-${r.id}`}>✕</button>
            </div>
          ))}
          <form onSubmit={add} className="flex flex-wrap gap-2 mt-2 items-center" data-testid="rewards-add-form">
            <input value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} maxLength={4} className="w-10 text-center border-b-2 bg-transparent" style={{ borderColor: INK }} data-testid="rewards-add-emoji" />
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ny belöning, t.ex. Bad i badhuset" maxLength={60} className="flex-1 min-w-[140px] border-b-2 bg-transparent px-1 text-sm" style={{ borderColor: INK, fontFamily: BODY }} data-testid="rewards-add-title" />
            <input type="number" min={1} max={200} value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="w-16 border-b-2 bg-transparent px-1 text-sm" style={{ borderColor: INK }} data-testid="rewards-add-cost" />
            <button type="submit" className="px-3 py-1 text-xs font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: OLIVE }} data-testid="rewards-add-submit">Lägg till</button>
          </form>
        </div>
      )}
    </div>
  );
}
